import { useEffect, useState } from 'react';
import { api, type OmenToday } from '../api';

/**
 * Front-page streak widget — flame + this week's Mon–Sun draws.
 *
 * STA-148 — rewired from /daily-checkin (love/wealth/health picker) to
 * /daily-omen (one-sentence note + verdict). The inline draw form moves
 * out: DailyOmen.tsx is the canonical draw surface, and this pill is
 * passive — it shows streak + week strip + a hint to scroll down when
 * today's omen hasn't been drawn yet. It refreshes when DailyOmen
 * dispatches the ``mp-omen-drawn`` custom event so the streak / week
 * strip / today marker update without a page reload.
 */
export default function CheckinPill() {
  const [data, setData] = useState<OmenToday | null>(null);

  const reload = async () => {
    try {
      const r = await api.omenToday();
      setData(r);
    } catch {
      setData(null);
    }
  };

  useEffect(() => {
    void reload();
    const onDrawn = () => void reload();
    window.addEventListener('mp-omen-drawn', onDrawn);
    return () => window.removeEventListener('mp-omen-drawn', onDrawn);
  }, []);

  if (!data) return null;

  const todayIso = new Date().toISOString().slice(0, 10);
  const checked = data.checked_in;

  return (
    <div className="max-w-3xl mx-auto">
      <div
        className={`w-full rounded-2xl border ${
          checked ? 'border-tarot-gold/50' : 'border-tarot-gold'
        } bg-tarot-card px-4 py-3 sm:px-5 sm:py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6`}
      >
        <div className="flex items-center gap-3 sm:min-w-[8rem]">
          <FlameIcon />
          <div className="leading-none">
            <div className="text-3xl sm:text-4xl font-semibold text-orange-400">
              {data.consecutive_days}
            </div>
            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-tarot-muted mt-1">
              Day streak
            </div>
          </div>
        </div>

        <div className="hidden sm:block w-px self-stretch bg-tarot-purple/40" />
        <div className="sm:hidden h-px w-full bg-tarot-purple/30" />

        <div className="flex-1 w-full grid grid-cols-7 gap-1 sm:gap-2">
          {data.week.map((d) => {
            const isToday = d.date === todayIso;
            return (
              <DayCell
                key={d.date}
                weekday={weekdayLetter(d.date)}
                checked={d.checked}
                isToday={isToday}
              />
            );
          })}
        </div>
      </div>

      {!checked && (
        <p className="text-center text-xs text-tarot-muted mt-2">
          ↓ 今天的籤還沒抽，先寫一句話再翻牌 ↓
        </p>
      )}
    </div>
  );
}

function DayCell({
  weekday,
  checked,
  isToday,
}: {
  weekday: string;
  checked: boolean;
  isToday: boolean;
}) {
  const letterClass = isToday
    ? 'text-orange-400 font-semibold'
    : 'text-tarot-muted';

  let circle: string;
  let content: React.ReactNode;
  if (isToday && checked) {
    circle =
      'bg-tarot-gold border-tarot-gold shadow-[0_0_18px_rgba(232,201,125,0.55)]';
    content = <StarIcon className="text-tarot-bg" />;
  } else if (isToday) {
    circle = 'border-tarot-gold border-dashed';
    content = null;
  } else if (checked) {
    circle = 'bg-rose-200 border-rose-200';
    content = <StarIcon className="text-tarot-bg" />;
  } else {
    circle = 'border-tarot-muted/40 border-dashed';
    content = null;
  }

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className={`text-[11px] sm:text-xs ${letterClass}`}>{weekday}</div>
      <div
        className={`w-7 h-7 sm:w-9 sm:h-9 rounded-full border-2 flex items-center justify-center ${circle}`}
      >
        {content}
      </div>
    </div>
  );
}

function FlameIcon() {
  return (
    <svg
      width="32"
      height="40"
      viewBox="0 0 32 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M16 1 C 18 9, 25 13, 25 21 C 25 28, 21 33, 16 33 C 11 33, 7 28, 7 21 C 7 16, 11 14, 12 9 C 13 14, 16 14, 16 1 Z"
        fill="url(#flame-grad)"
      />
      <path
        d="M16 13 C 17 18, 20 20, 20 24 C 20 28, 18 30, 16 30 C 14 30, 12 28, 12 24 C 12 21, 14 20, 14 17 C 15 20, 16 19, 16 13 Z"
        fill="#FFD06A"
        opacity="0.85"
      />
      <defs>
        <linearGradient id="flame-grad" x1="16" y1="1" x2="16" y2="33" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FFB347" />
          <stop offset="1" stopColor="#FF5E3A" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function StarIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path d="M10 1.5 L12.6 7.3 L19 8 L14.3 12.4 L15.6 18.6 L10 15.5 L4.4 18.6 L5.7 12.4 L1 8 L7.4 7.3 Z" />
    </svg>
  );
}

function weekdayLetter(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  return ['S', 'M', 'T', 'W', 'T', 'F', 'S'][d.getUTCDay()];
}
